import { useEffect } from 'hono/jsx';
import { AuthProvider, useAuth } from './lib/auth';
import { matchRoute, navigate, useRoute } from './lib/router';
import { ToastHost } from './components/ToastHost';
import { Spinner } from './components/Spinner';
import { Login } from './pages/Login';
import { SitesList } from './pages/SitesList';
import { SiteNew } from './pages/SiteNew';
import { SiteBuild } from './pages/site/Build';
import { SiteFiles } from './pages/site/Files';
import { SiteBackups } from './pages/site/Backups';
import { SiteVersions } from './pages/site/Versions';
import { SiteDomain } from './pages/site/Domain';
import { SiteBilling } from './pages/site/Billing';
import { SiteSettings } from './pages/site/Settings';
import { Account } from './pages/Account';
import { AffiliateDashboard } from './pages/Affiliate';
import { AdminSites } from './pages/admin/Sites';
import { AdminUsers } from './pages/admin/Users';
import { AdminAffiliates } from './pages/admin/Affiliates';
import { AdminPlans } from './pages/admin/Plans';
import { AdminCommissions } from './pages/admin/Commissions';
import { AdminEmails } from './pages/admin/Emails';
import { Pay } from './pages/pay/Pay';
import { PaySuccess } from './pages/pay/Success';

export function App() {
  return (
    <AuthProvider>
      <Router />
      <ToastHost />
    </AuthProvider>
  );
}

function Router() {
  const path = useRoute();
  const { user, loading } = useAuth();

  // Public payment links resolve regardless of auth.
  const paySuccess = matchRoute(path, '/pay/:token/success');
  if (paySuccess) return <PaySuccess token={paySuccess.token} />;
  const pay = matchRoute(path, '/pay/:token');
  if (pay) return <Pay token={pay.token} />;

  if (loading) return <BootSpinner />;
  if (!user) return <Login />;

  // Default + back-compat. /dashboard → /sites.
  const onlyPath = path.split('?')[0];
  if (onlyPath === '/' || onlyPath === '' || onlyPath === '/dashboard') {
    return <Redirect to="/sites" />;
  }

  if (matchRoute(path, '/sites')) return <SitesList />;
  if (matchRoute(path, '/sites/new')) return <SiteNew />;

  // /sites/:id/purchase → /sites/:id/billing back-compat.
  const purchase = matchRoute(path, '/sites/:id/purchase');
  if (purchase) return <Redirect to={`/sites/${purchase.id}/billing`} />;

  // Site sub-pages — order static segments before bare :id so they win.
  const buildAlias = matchRoute(path, '/sites/:id/build');
  if (buildAlias) return <SiteBuild siteId={buildAlias.id} />;
  const files = matchRoute(path, '/sites/:id/materials');
  if (files) return <SiteFiles siteId={files.id} />;
  const backups = matchRoute(path, '/sites/:id/backups');
  if (backups) return <SiteBackups siteId={backups.id} />;
  const versions = matchRoute(path, '/sites/:id/versions');
  if (versions) return <SiteVersions siteId={versions.id} />;
  const domain = matchRoute(path, '/sites/:id/domain');
  if (domain) return <SiteDomain siteId={domain.id} />;
  const billing = matchRoute(path, '/sites/:id/billing');
  if (billing) return <SiteBilling siteId={billing.id} />;
  const settings = matchRoute(path, '/sites/:id/settings');
  if (settings) return <SiteSettings siteId={settings.id} />;
  const siteRoot = matchRoute(path, '/sites/:id');
  if (siteRoot) return <SiteBuild siteId={siteRoot.id} />;

  if (matchRoute(path, '/account')) return <Account />;
  if (matchRoute(path, '/affiliate')) return <AffiliateDashboard />;
  if (matchRoute(path, '/admin/users')) return <AdminUsers />;
  if (matchRoute(path, '/admin') || matchRoute(path, '/admin/sites')) return <AdminSites />;
  if (matchRoute(path, '/admin/affiliates')) return <AdminAffiliates />;
  if (matchRoute(path, '/admin/plans')) return <AdminPlans />;
  if (matchRoute(path, '/admin/commissions')) return <AdminCommissions />;
  if (matchRoute(path, '/admin/emails')) return <AdminEmails />;

  return <NotFound />;
}

function BootSpinner() {
  return (
    <div class="min-h-screen flex items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

function NotFound() {
  return (
    <div class="min-h-screen flex flex-col items-center justify-center text-center p-8">
      <h1 class="text-2xl font-semibold text-slate-100 mb-2">Page not found</h1>
      <p class="text-slate-400 mb-4">This dashboard route isn't recognised.</p>
      <a
        href="/sites"
        onClick={(e: MouseEvent) => { e.preventDefault(); navigate('/sites'); }}
        class="text-cyan-400 hover:text-cyan-300 text-sm"
      >Go to your sites</a>
    </div>
  );
}

function Redirect({ to }: { to: string }) {
  useEffect(() => { navigate(to); }, [to]);
  return <BootSpinner />;
}
